import cn from "classnames";
import {
  useImperativeHandle,
  useState,
  useRef,
  forwardRef,
  Dispatch,
  SetStateAction,
  useEffect,
} from "react";

import { isNotEmpty } from "../../utils/obj_utils";

import styles from "./table.module.css";
import Input from "../input";
import getCopy from "../../data/copy";
import Button from "../button";
import { EIconSize, TrashIcon } from "../icons";
import SearchForm from "../search_form";
import { signUpUserWithEmail } from "../../libs/auth/cognito";

export type TCellValue = string | number | null | undefined;

export type ComponentSelectOptions = {
  c: any;
  display_name: string;
  syntensor_id?: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TCellRenderer = (
  _: any,
  __: any,
  ___: any,
  index: number,
  tableData: Record<string, any>[],
  setTableData: Dispatch<SetStateAction<Record<string, any>[]>>
) => any;

export type ValidateCellFunction = (datum?: any) => Promise<boolean>;

export type LookupCellFunction = (datum?: any) => Promise<any>;

export type ItemSelectedHandler = (
  suggestion: SuggestionType,
  datum?: any,
  selectCellDataId?: string,
  col?: ITableCol,
  setTableData?: Dispatch<SetStateAction<Record<string, any>[]>>,
  i?: number
) => Promise<any>;

export interface ITableCol {
  id?: string;
  name?: string;
  templateDescription?: any;
  className?: string;
  colSpan?: number;
  width?: number | string;
  placeholder?: string;
  cellRenderer?: TCellRenderer;
  inputCellRenderer?: TCellRenderer;
  lookupHandler?: LookupCellFunction;
  validateHandler?: ValidateCellFunction;
  itemSelected?: ItemSelectedHandler;
  headerRenderer?: (
    col: ITableCol,
    data: any,
    i?: number
  ) => React.ReactElement;
  // (
  //   d: Record<string, TCellValue>,
  //   datum: TCellValue,
  //   col: ITableCol | string,
  //   i: number
  // ) => ReactElement;
}

export type SuggestionType = any;

export interface ITableProps {
  config: {
    cols?: ITableCol[];
    allowEditing?: boolean;
  };
  data: Record<string, any>[];
}

export function defaultCellRenderer(d: TCellValue) {
  const isNull = typeof d === "undefined" || d === null || d === "";
  let val: any;
  if (!isNull) {
    val = d;

    if (typeof val === "string") {
      const emailRegex =
        /^[^\s@]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/;
      if (emailRegex.test(val)) {
        val = <a href="mailto:{val}">{val}</a>;
      }
    }
  } else {
    val = "-";
  }

  return val;
}

export function linkCellRenderer(d: string) {
  return (
    <a className={styles.link} href={d} target="_blank" rel="noreferrer">
      {d}
    </a>
  );
}

const lookupCell = async (
  col: ITableCol,
  datum: any,
  i: number,
  setTableData: Dispatch<SetStateAction<Record<string, any>[]>>
): Promise<any> => {
  const validatingId = `validating${col.id}`;
  if (datum[validatingId]) {
    return datum;
  }

  datum[validatingId] = true;

  setTableData((prevData) => {
    const newData = [...prevData];
    newData[i] = datum;
    return newData;
  });

  datum = await col.lookupHandler!(datum);
  datum[validatingId] = false;

  setTableData((prevData) => {
    const newData = [...prevData];
    newData[i] = datum;
    return newData;
  });

  return datum;
};

const validateCell = async (
  col: ITableCol,
  datum: any,
  i: number,
  setTableData: Dispatch<SetStateAction<Record<string, any>[]>>
): Promise<any> => {
  const validatingId = `validating${col.id}`;
  if (datum[validatingId]) {
    return datum;
  }

  datum[validatingId] = true;
  setTableData((prevData) => {
    const newData = [...prevData];
    newData[i] = datum;
    return newData;
  });

  const invalidId = `invalid${col.id}`;
  const result = await col.validateHandler!(datum);
  datum[invalidId] = result;
  datum[validatingId] = false;

  setTableData((prevData) => {
    const newData = [...prevData];
    newData[i] = datum;
    return newData;
  });

  return datum;
};

export function selectCellRenderer(
  value: TCellValue,
  datum: Record<string, any> = {},
  col: ITableCol,
  i: number,
  _: Record<string, any>[],
  setTableData: Dispatch<SetStateAction<Record<string, any>[]>>
) {
  const validatingId = `validating${col.id}`;
  const invalidId = `invalid${col.id}`;
  const selectCellDataId = `selectCellData${col.id}`;
  let changeTimer: NodeJS.Timeout;

  let borderStyle = datum[invalidId] ? { border: "1px solid red" } : {};

  if (datum[validatingId]) {
    borderStyle = { border: "1px solid yellow" };
  }

  const itemSelected = async (suggestion: SuggestionType) => {
    await col.itemSelected!(
      suggestion,
      datum,
      selectCellDataId,
      col,
      setTableData,
      i
    );
  };

  async function blurHandler() {
    clearTimeout(changeTimer);

    if (col.validateHandler) {
      datum = await validateCell(col, datum, i, setTableData);
      if (!datum[invalidId]) {
        if (col.lookupHandler) {
          datum = await lookupCell(col, datum, i, setTableData);
        }
      }
    }
  }

  return (
    <div style={borderStyle} data-testid="header_search">
      <SearchForm<SuggestionType>
        isLoading={false}
        placeholder={col.placeholder}
        suggestions={datum[selectCellDataId]}
        defaultInputValue={datum[col.id!]}
        hasSubmitBtn={false}
        hasCancelBtn={false}
        onChange={(input: SuggestionType) => {
          datum[col.id!] = input;
          setTableData((prevData) => {
            const newData = [...prevData];
            newData[i] = datum;
            return newData;
          });

          clearTimeout(changeTimer);
          changeTimer = setTimeout(async () => {
            await blurHandler();
          }, 500);
        }}
        searchIcon={false}
        onSearch={undefined}
        onCancel={undefined}
        onClose={undefined}
        onFocus={undefined}
        onBlur={undefined}
        onInputFocus={() => {
          clearTimeout(changeTimer);
          changeTimer = setTimeout(async () => {
            await blurHandler();
          }, 500);
        }}
        onItemSelected={itemSelected}
        autoComplete="off"
        name="componentSearch-search"
        inputRef={null}
        afterChildren={undefined}
      />
    </div>
  );
}

export function inputCellRenderer(
  value: TCellValue,
  datum: Record<string, any> = {},
  col: ITableCol,
  i: number,
  _: Record<string, any>[],
  setTableData: Dispatch<SetStateAction<Record<string, any>[]>>
) {
  const validatingId = `validating${col.id}`;
  const invalidId = `invalid${col.id}`;
  let changeTimer: NodeJS.Timeout;

  let borderStyle = datum[invalidId] ? { border: "1px solid red" } : {};

  if (datum[validatingId]) {
    borderStyle = { border: "1px solid yellow" };
  }

  async function blurHandler() {
    clearTimeout(changeTimer);

    if (col.validateHandler) {
      datum = await validateCell(col, datum, i, setTableData);

      if (!datum[invalidId]) {
        if (col.lookupHandler) {
          datum = await lookupCell(col, datum, i, setTableData);
        }
      }
    }
  }

  return (
    <Input
      key={`${col.id}${i}`}
      type="text"
      value={datum[col.id!] ?? ""}
      placeholder={col.placeholder ?? ""}
      style={borderStyle}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        datum[col.id!] = e.target.value;

        setTableData((prevData) => {
          const newData = [...prevData];
          newData[i] = datum;
          return newData;
        });

        clearTimeout(changeTimer);
        changeTimer = setTimeout(async () => {
          await blurHandler();
        }, 500);
      }}
    />
  );
}

export function deleteCellRenderer(
  value: TCellValue,
  datum: any,
  col: ITableCol,
  i: number,
  _: Record<string, any>[],
  setTableData: Dispatch<SetStateAction<Record<string, any>[]>>
) {
  return (
    <button
      className={styles.endpointFormRemoveBtn}
      onClick={() => {
        const confirmed = window.confirm(
          getCopy("studies_create_compound-delete-confirmation")
        );
        if (confirmed) {
          setTableData((prevData) => {
            const newData = [...prevData];
            newData.splice(i, 1);
            return newData;
          });
        }
      }}
      type="button"
    >
      <TrashIcon size={EIconSize.SMALL} />
    </button>
  );
}

export function descriptionCellRenderer(d: TCellValue) {
  return <span className={styles.description}>{d}</span>;
}

export interface TableRef {
  getInternalState: () => Record<string, any>[];
  setInternalState: (newData: Record<string, any>[]) => void;
}

const Table = forwardRef<TableRef, ITableProps>((props, ref) => {
  const { cols, allowEditing } = props.config;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tableData, setTableData] = useState(props.data);

  useEffect(() => {
    if (props.data && props.data.length && tableData.length === 0) {
      setTableData(props.data);
    }

    if (!allowEditing) {
      // let the external state update internal state
      setTableData(props.data);
    }
  }, [props.data, tableData]);

  useImperativeHandle(ref, () => ({
    getInternalState: () => {
      return structuredClone(tableData);
    },
    setInternalState: (newData: Record<string, any>[]) => {
      setTableData(newData);
    },
  }));

  const handleUploadButtonClick = () => {
    fileInputRef.current?.click();
  };

  const generateTemplate = () => {
    const filteredCols = cols?.filter((c) => c.id !== "delete") ?? [];
    const content = [
      `${filteredCols.map((c) => c.name).join("\t")}`,
      `${filteredCols.map((c) => c.templateDescription).join("\t")}`,
      "",
    ];
    const blob = new Blob([content.join("\n")], { type: "text/plain" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "template.tsv";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(link.href);
  };

  const isTsvFile = (file: File): boolean => {
    return file.type === "text/tsv" || file.name.toLowerCase().endsWith(".tsv");
  };

  const readFileContent = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        resolve(event.target?.result as string);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsText(file);
    });
  };

  const parseTSV = (tsv: string[]) => {
    const startingTableLength = tableData.length;
    const newDataRows: Record<string, any>[] = tableData;

    tsv.splice(1, tsv.length).forEach((line: string, lineNumber) => {
      const fields = line.split("\t");
      let newData: Record<string, any> = {};

      cols?.forEach(async (col: ITableCol, index: number) => {
        newData[col.id!] = fields[index];

        if (col.validateHandler) {
          newData = await validateCell(
            col,
            newData,
            lineNumber + startingTableLength,
            setTableData
          );

          if (col.lookupHandler) {
            newData = await lookupCell(
              col,
              newData,
              lineNumber + startingTableLength,
              setTableData
            );
          }
        }
      });

      newDataRows.push(newData);
    });

    setTableData(newDataRows);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.style.backgroundColor = "var(--primary-gray)";
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.style.backgroundColor = "var(--primary-light)";
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    event.preventDefault();

    const files = event.target.files;
    if (files) {
      await processFiles(files);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    handleDragLeave(event);

    const files = event.dataTransfer.files;
    await processFiles(files);
  };

  const processFiles = async (files: FileList) => {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (isTsvFile(file)) {
        try {
          const content = await readFileContent(file);
          parseTSV(
            content.split("\n").filter((line: string) => line.length > 0)
          );
        } catch (error) {
          console.error("Error reading file:", error);
        }
      } else {
        console.warn("The dropped file is not a TSV file.");
      }
    }
  };

  const addBlankRow = () => {
    let newData: Record<string, any> = {};

    cols
      ?.filter((col) => col.inputCellRenderer)
      .forEach(async (col) => {
        newData[col.id ?? ""] = "";

        if (col.validateHandler) {
          newData = await validateCell(
            col,
            newData,
            tableData.length,
            setTableData
          );
        }
      });

    setTableData([...tableData, newData]);
  };

  if (allowEditing && tableData.length === 0) {
    addBlankRow();
  }

  return (
    <div
      onDragOver={allowEditing ? handleDragOver : undefined}
      onDragLeave={allowEditing ? handleDragLeave : undefined}
      onDrop={allowEditing ? handleDrop : undefined}
    >
      {allowEditing && (
        <div className={styles.titleWrapper}>
          <div className={styles.buttonsWrapper}>
            <Button role="subtle" onClick={addBlankRow}>
              Add New
            </Button>

            <Button role="subtle" onClick={generateTemplate}>
              TSV Template
            </Button>

            <Button role="primary" onClick={handleUploadButtonClick}>
              Upload a TSV
            </Button>

            <input
              type="file"
              accept=".tsv"
              multiple={true}
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
          </div>
        </div>
      )}

      <table className={styles.table} data-testid="table">
        <thead>
          <tr>
            {cols &&
              cols.map((col, i) => {
                const colId = col && typeof col !== "string" ? col.id : col;
                const colName = col && typeof col !== "string" ? col.name : col;
                const colSpan =
                  col && typeof col !== "string" && col.colSpan
                    ? col.colSpan
                    : 1;

                //  we want to allow to render more complex header cells (e.g.)
                const rendererHeaderCell =
                  col && typeof col.headerRenderer === "function"
                    ? col.headerRenderer(col, i)
                    : colName;

                const style: Record<string, string | number> = {};
                if (typeof col !== "string" && col.width) {
                  style.width = col.width;
                }

                return (
                  <th
                    key={colId}
                    colSpan={colSpan}
                    style={style}
                    className={styles.th}
                  >
                    {rendererHeaderCell}
                  </th>
                );
              })}
          </tr>
        </thead>
        <tbody>
          {tableData &&
            tableData.map((datum, i: number) => {
              return (
                <tr key={`tr-${i}`}>
                  {cols &&
                    cols.map((col, i2: number) => {
                      const colId =
                        col && typeof col !== "string" ? col.id : col;
                      const cellValue =
                        colId && datum && isNotEmpty(datum[colId])
                          ? datum[colId]
                          : "";
                      const className =
                        (col && typeof col !== "string" && col.className) || "";

                      const colClassNames = cn(styles.td, {
                        [className]: !!className,
                      });
                      const cellRenderer =
                        (typeof col !== "string" &&
                          (allowEditing
                            ? col.inputCellRenderer
                              ? col.inputCellRenderer
                              : col.cellRenderer
                            : col.cellRenderer)) ||
                        defaultCellRenderer;

                      //  could be potentially rendering more cells
                      if (cellValue && Array.isArray(cellValue)) {
                        return cellValue.map((val, i3) => {
                          return (
                            <td key={i3} className={colClassNames}>
                              {cellRenderer(
                                val,
                                datum,
                                col,
                                i3,
                                tableData,
                                setTableData
                              )}
                            </td>
                          );
                        });
                      }

                      //  render single cell
                      return (
                        <td key={i2} className={colClassNames}>
                          {cellRenderer(
                            cellValue,
                            datum,
                            col,
                            i,
                            tableData,
                            setTableData
                          )}
                        </td>
                      );
                    })}
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
});

Table.displayName = "Table";
export default Table;
